import React, { createContext, useState, useContext, useEffect } from 'react';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { get, post } from '../hooks/apiFetch';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [authState, setAuthState] = useState({
        isAuthenticated: false,
        user: null,
        permissions: new Set(),
        isLoading: true,
    });

    const fetchUserPermissions = async () => {
        try {
            const permissions = await get('/api/auth/UserPermissions');
            return permissions || [];
        } catch (error) {
            console.error('Failed to fetch permissions', error);
            return [];
        }
    };

    const fetchUserData = async () => {
        try {
            const userData = await get('/api/UserDb/UserData');
            const permissions = await fetchUserPermissions();
            setAuthState({
                isAuthenticated: true,
                user: {
                    fullname: userData.fullName,
                    email: userData.login,
                },
                permissions: new Set(permissions),
                isLoading: false,
            });
        } catch (error) {
            console.error('Auth check failed', error);
            setAuthState({
                isAuthenticated: false,
                user: null,
                permissions: new Set(),
                isLoading: false,
            });
        }
    };

    useEffect(() => {
        fetchUserData();
    }, []);

    const login = async (credentials) => {
        // Предположим, что ваш логин-эндпоинт устанавливает HttpOnly-куку
        try {
            await post('/api/auth/Authentication');
            // После успешного логина запрашиваем данные пользователя
            await fetchUserData();
        } catch (error) {
            console.error('Login failed', error);
            throw error;
        }
    };

    const logout = async () => {
        try {
            const fp = await FingerprintJS.load();
            const { visitorId } = await fp.get();
            await post('/api/auth/Logout', visitorId); // сервер удалит куку

        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            setAuthState({
                isAuthenticated: false,
                user: null,
                permissions: new Set(),
                isLoading: false,
            });
        }
    };

    const checkPermission = (requiredPermission) => {
        return authState.permissions.has(requiredPermission);
    };

    const refreshUser = async () => {
        await fetchUserData();
    };

    return (
        <AuthContext.Provider value={{ ...authState, checkPermission, login, logout, refreshUser }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const withAuth = (Component) => {
    return function WrappedComponent(props) {
        const auth = useAuth();
        return <Component {...props} auth={auth} />;
    };
};